import { useForm } from '@mantine/form';

import { prophetTypes } from '../types';
import { ChunkPlatform } from '../utils/chunk-limits';
import { validations } from '../validations';

import config from '@/config';

export type ProphetRuleForm = ReturnType<typeof useProphetRuleForm>;

type Props = {
  initialValues?: Omit<prophetTypes.Rule, 'page' | '_id'>;
  // Per-platform chunk limit context. instagram = byte gate, telegram =
  // char gate (see utils/chunk-limits.ts). Defaults to 'telegram' for
  // backward compat with callers that don't pass it yet.
  platform?: ChunkPlatform;
};

export const useProphetRuleForm = ({ initialValues, platform = 'telegram' }: Props = {}) => {
  return useForm<Omit<prophetTypes.Rule, 'page' | '_id'>>({
    initialValues: {
      keyword: initialValues?.keyword || '',
      requirements:
        initialValues?.requirements.map((r) => ({
          ...r,
          ...(r.type !== 'text' && { count: r.count.map((c) => String(c)) as any }),
        })) || [],
      actions: {
        success: initialValues?.actions.success ?? {
          type: 'sendToManager',
          funnelId: '',
        },
        fail: initialValues?.actions.fail || 'sendToManager',
        noResponse: initialValues?.actions.noResponse || 'remove',
      },
      intervals: {
        requirements: initialValues?.intervals.requirements || 23,
      },
      messages: {
        greetings: initialValues?.messages?.greetings || [],
        preparing: initialValues?.messages?.preparing || [],
        discounts: initialValues?.messages?.discounts || [],
        spreads: initialValues?.messages?.spreads || [],
        spreadKicks: initialValues?.messages?.spreadKicks || [],
      },
      isDiscountRequired: initialValues?.isDiscountRequired ?? true,
      isSpreadKickRequired: initialValues?.isSpreadKickRequired ?? false,
    },

    clearInputErrorOnChange: true,

    transformValues: (values) => {
      return {
        ...values,
        requirements: values.requirements.map((r) => ({
          ...r,
          ...(r.type !== 'text' && { count: r.count.map((c) => Number(c)) }),
        })),
      };
    },

    validate: {
      keyword: (v) =>
        initialValues?.keyword ? validations.isRequired(v, 'Кодовое слово') : undefined,
      actions: (v) =>
        validations.isRequired(v.success.type, 'Успешное действие') &&
        validations.isRequired(v.fail, 'Неудачное действие') &&
        validations.isRequired(v.noResponse, 'Действие при отсутствии ответа на требования'),
      requirements: (v) => {
        return (
          v.reduce((acc, r) => {
            if (!r) {
              return 'Не правильный формат';
            }
            if (!['date', 'image', 'text'].includes(r.type)) {
              return 'Не правильный тип';
            }
            if (r.type === 'text' && !r.texts.length) {
              return 'Не хватает текста уточнения';
            }
            if (r.type !== 'text' && !r.count.length) {
              return 'Не хватает количества';
            }
            if (r.texts.length && r.texts.some((t) => !t)) {
              return 'Не правильный текст уточнения';
            }
            return acc;
          }, '') || undefined
        );
      },
      intervals: (v) => {
        if (typeof v?.requirements !== 'number') {
          return 'Не правильный формат';
        }
        const minRequirements = config.isProd ? 0.1 : 0.001;
        if (v.requirements < minRequirements) {
          return `Минимальное значение ${minRequirements}`;
        }
      },
      messages: (v, values) => {
        if (values.actions.success.type !== 'reply') {
          return;
        }
        return (
          // Greetings are optional (DEV-2627) — empty list sends the first
          // reply without a greeting prefix.
          validations.messagesArray('Расклады', v.spreads) ||
          (values.isDiscountRequired && validations.messagesArray('Скидки', v.discounts)) ||
          (values.isSpreadKickRequired && validations.messagesArray('Пинки', v.spreadKicks)) ||
          validations.messages('Приветствия', v.greetings, ['text'], platform) ||
          validations.messages('Подготовка', v.preparing, undefined, platform) ||
          validations.messages('Расклады', v.spreads, undefined, platform) ||
          (values.isDiscountRequired &&
            validations.messages('Скидки', v.discounts, undefined, platform)) ||
          validations.messages('Пинки', v.spreadKicks, undefined, platform) ||
          undefined
        );
      },
    },
  });
};
